'use client';

import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    type IdentityUser,
    type UserData,
    type UserDataToUpdate,
    UserSessionProvider,
    type UserSessionProviderContextProps,
    decodeJwtCookie,
    PageType,
    getCookieValue,
    useStoreDispatch,
} from '@libs/common-next';
import type { UpcomingOrderGrooveDetails } from '@libs/common-node';
import { AUTH_COOKIES, logger, stringifyStoreId, type Nullable } from '@libs/core';
import apiProxyClient from '@libs/api-proxy-client';
import { getAppointments, getPackages, getAddresses } from '@libs/api-proxy-client/v2';
import { AccountAPI } from '@libs/experience-api';
import { AutoshipDataProvider } from '@app/acp/components/account/Autoship/AutoshipDataProvider';
import { initSession } from '@libs/ocapi/server';
import { getOrderGrooveSubscriptionInfo } from '@libs/order-groove/server';
import { useUserSessionLocalStorage } from '../../hooks/useUserSessionLocalStorage';
import { PROFILE_DATA } from '../../constants';
import { getSearchStores } from '@libs/common-next';

const refreshOcapi = initSession;

const loadingState = {
    isLoading: true,
};

interface PageTypeProps {
    pageType?: Nullable<PageType>;
}

export default function UserSessionWrapper({ children, pageType }: PropsWithChildren<PageTypeProps>) {
    const [ocapi, setOcapi] = useState<UserSessionProviderContextProps['ocapi']>(loadingState);

    const [loyalty, setLoyalty] = useState<UserSessionProviderContextProps['loyalty']>();
    const [orderGrooveData, setOrderGrooveData] = useState<Nullable<UpcomingOrderGrooveDetails>>();
    const [user, setUser] = useState<UserSessionProviderContextProps['user']>(
        // set initial state
        () => decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN)
    );
    const [userData, setUserData] = useState<UserSessionProviderContextProps['userData']>();

    const [isAuthenticated, setIsAuthenticated] = useState<UserSessionProviderContextProps['isAuthenticated']>();

    // TODO: remove all ocapi usage after go-live
    const customer = ocapi.data?.session?.customer;
    const isAuthenticatedOcapi = customer?.auth_type === 'registered';
    const basket = ocapi.data?.basket;

    const [storageUserSession] = useUserSessionLocalStorage({
        customer,
        loyalty,
        basket,
    });

    const isCartPage = pageType === PageType.CART;

    const getUserData = useCallback(async () => {
        const [addressesResult, appointmentsResult, packagesResult, paymentsResult, profileResult] =
            await Promise.allSettled(
                isCartPage
                    ? [null, null, null, null, AccountAPI.Client.getProfile()]
                    : [
                          getAddresses(),
                          getAppointments(),
                          getPackages(),
                          AccountAPI.Client.getPayments(),
                          AccountAPI.Client.getProfile(),
                      ]
            );

        function getResult<T>(result: PromiseSettledResult<T>) {
            return result.status === 'rejected' ? null : result.value;
        }

        const addresses = getResult(addressesResult);
        const appointments = getResult(appointmentsResult);
        const packages = getResult(packagesResult);
        const payments = getResult(paymentsResult);
        const profile = getResult(profileResult);

        const newUserData = {
            addresses,
            appointments,
            packages,
            payments,
            profile,
        };

        setUserData(prev => ({
            ...(prev as UserData),
            ...(newUserData as UserData),
        }));
    }, [isCartPage]);

    /** Rename it to 'updateUserData' when ready to tackle test coverage */
    const updateProfileData = useCallback(
        async (dataToUpdate?: string) => {
            function updateState(key: string, value: UserDataToUpdate) {
                setUserData(prev => ({
                    ...(prev as UserData),
                    [key]: value,
                }));
            }

            switch (dataToUpdate) {
                case PROFILE_DATA.ADDRESSES: {
                    const addresses = await getAddresses();
                    return updateState(PROFILE_DATA.ADDRESSES, addresses);
                }
                case PROFILE_DATA.APPOINTMENTS: {
                    const appointments = await getAppointments();
                    return updateState(PROFILE_DATA.APPOINTMENTS, appointments);
                }
                case PROFILE_DATA.PACKAGES: {
                    const packages = await getPackages();
                    return updateState(PROFILE_DATA.PACKAGES, packages);
                }
                case PROFILE_DATA.PAYMENTS: {
                    const payments = await AccountAPI.Client.getPayments();
                    return updateState(PROFILE_DATA.PAYMENTS, payments);
                }
                case PROFILE_DATA.PROFILE: {
                    const profile = await AccountAPI.Client.getProfile();
                    return updateState(PROFILE_DATA.PROFILE, profile);
                }
                default: {
                    return getUserData();
                }
            }
        },
        [getUserData]
    );

    // getStoreCookie function adapted for client-side usage
    const getStoreCookie = (): string => {
        if (IS_SSR) {
            return '';
        }
        const storeCookie = getCookieValue('StoreCookie');
        if (!storeCookie) {
            return '';
        }
        const parsedStoreId = parseInt(storeCookie, 10);
        return isNaN(parsedStoreId) ? '' : parsedStoreId.toString();
    };

    const { updateSelectedStore } = useStoreDispatch();

    useEffect(() => {
        async function initStore() {
            try {
                let storeId = getStoreCookie();
                if (!storeId) {
                    // no store cookie, get closest stores by IP
                    const closestStores = await getSearchStores({});
                    if (closestStores.length > 0) {
                        const storeNumberStr = stringifyStoreId(String(closestStores[0].Store.StoreNumber));
                        if (storeNumberStr) {
                            updateSelectedStore(storeNumberStr);
                            storeId = storeNumberStr;
                        }
                    }
                }
                if (!storeId) {
                    logger.warn('failed to get store id -- both from cookie and search call for closest stores');
                }
            } catch (e) {
                logger.warn('failed store sync:', e);
            }
        }

        initStore();
        // TODO: remove OCAPI state now that we're on live full solution
        setOcapi({
            isLoading: false,
            data: null,
        });
    }, [updateSelectedStore]);

    // TODO: remove effect after go-live
    useEffect(() => {
        async function loyaltyInit() {
            if (customer?.c_petmToken) {
                const result = await apiProxyClient.loyalty.getLoyaltyPoints(customer?.c_petmToken);
                setLoyalty(result);
            }
        }

        loyaltyInit();
    }, [customer?.c_petmToken]);

    useEffect(() => {
        async function orderGrooveInit() {
            if (isAuthenticated) {
                const result = await getOrderGrooveSubscriptionInfo(
                    isAuthenticated === 'velocity' ? user?.sid : customer?.c_janrainIdentifier // as the sid of the new implemented user session is the same as c_janrainIdentifier, we're checking if user logged in with new experience passing sid if not the legacy identifier
                );
                setOrderGrooveData(result);
            }
        }

        orderGrooveInit();
    }, [customer?.c_janrainIdentifier, isAuthenticated, user?.sid]);

    useEffect(() => {
        if (user) {
            getUserData();
        }
    }, [user, getUserData]);

    useEffect(() => {
        if (user) {
            setIsAuthenticated('velocity');
        } else if (isAuthenticatedOcapi) {
            setIsAuthenticated('ocapi');
        } else {
            setIsAuthenticated(undefined);
        }
    }, [isAuthenticatedOcapi, user]);

    const value = useMemo(
        () => ({
            isAuthenticated,
            loyalty,
            ocapi,
            orderGrooveData,
            refreshOcapi,
            setOcapi,
            setUser,
            storageUserSession,
            user,
            userData,
            updateProfileData,
        }),
        [isAuthenticated, loyalty, ocapi, orderGrooveData, storageUserSession, user, userData, updateProfileData]
    );

    return (
        <UserSessionProvider values={value}>
            <AutoshipDataProvider>{children}</AutoshipDataProvider>
        </UserSessionProvider>
    );
}
